import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import './Demos.css';

const Demos = () => {
  const [activeDemo, setActiveDemo] = useState('api');
  const [apiResponse, setApiResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [jwtToken, setJwtToken] = useState('');
  const [wsMessage, setWsMessage] = useState('');
  const [wsMessages, setWsMessages] = useState([]);

  // Demo de API REST
  const fetchAPIDemo = async () => {
    setLoading(true);
    try {
      // Aquí conectarías con tu backend
      // Por ahora simulamos la respuesta
      setTimeout(() => {
        setApiResponse({
          status: 200,
          data: {
            message: 'Respuesta exitosa del servidor',
            timestamp: new Date().toISOString(),
            users: [
              { id: 1, name: 'Usuario 1', email: 'user1@example.com' },
              { id: 2, name: 'Usuario 2', email: 'user2@example.com' }
            ]
          }
        });
        setLoading(false);
      }, 1000);
    } catch (error) {
      setApiResponse({ error: 'Error en la petición' });
      setLoading(false);
    }
  };

  // Demo de JWT
  const generateJWT = () => {
    // Simulación de generación de JWT
    const mockToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';
    setJwtToken(mockToken);
  };

  // Demo de WebSocket
  const sendWebSocketMessage = () => {
    if (wsMessage.trim()) {
      const newMessage = {
        id: Date.now(),
        text: wsMessage,
        timestamp: new Date().toLocaleTimeString(),
        sender: 'Tú'
      };
      setWsMessages([...wsMessages, newMessage]);
      
      // Simular respuesta del servidor
      setTimeout(() => {
        setWsMessages(prev => [...prev, {
          id: Date.now(),
          text: `Eco: ${wsMessage}`,
          timestamp: new Date().toLocaleTimeString(),
          sender: 'Servidor'
        }]);
      }, 500);
      
      setWsMessage('');
    }
  };

  const demos = [
    { id: 'api', label: 'API REST', icon: '🌐' },
    { id: 'jwt', label: 'JWT Auth', icon: '🔐' },
    { id: 'websocket', label: 'WebSocket', icon: '⚡' },
    { id: 'swagger', label: 'Swagger', icon: '📚' }
  ];

  return (
    <section id="demos" className="section demos">
      <div className="container">
        <motion.h2 
          className="section-title gradient-text"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Interactive Demos
        </motion.h2>

        <motion.p 
          className="demos-subtitle"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Explore the technologies I work with through live interactive demos
        </motion.p>

        <div className="demos-tabs">
          {demos.map(demo => (
            <button
              key={demo.id}
              className={`demo-tab ${activeDemo === demo.id ? 'active' : ''}`}
              onClick={() => setActiveDemo(demo.id)}
            >
              <span className="tab-icon">{demo.icon}</span>
              {demo.label}
            </button>
          ))}
        </div>

        <motion.div 
          className="demo-content"
          key={activeDemo}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeDemo === 'api' && (
            <div className="demo-panel">
              <h3>REST API Request</h3>
              <p>Example of consuming a RESTful API</p> 
              <div className="demo-actions">
                <button 
                  className="btn-primary"
                  onClick={fetchAPIDemo}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'GET /api/users'}
                </button>
              </div>

              {apiResponse && (
                <div className="response-box">
                  <div className="response-header">
                    <span className="response-status">
                      Status: {apiResponse.status || 'Error'}
                    </span>
                  </div>
                  <pre className="response-body">
                    {JSON.stringify(apiResponse, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {activeDemo === 'jwt' && (
            <div className="demo-panel">
              <h3>JWT Authentication</h3>
              <p>Generation and validation of JSON Web Tokens</p>
              <div className="demo-actions">
                <button 
                  className="btn-primary"
                  onClick={generateJWT}
                >
                  Generate JWT Token
                </button>
              </div>

              {jwtToken && (
                <div className="jwt-display">
                  <label>Generated Token:</label>
                  <div className="token-box">
                    <code>{jwtToken}</code>
                  </div>
                  <div className="jwt-parts">
                    <div className="jwt-part header">
                      <span className="part-label">Header</span>
                      <code>{"{ alg: 'HS256', typ: 'JWT' }"}</code>
                    </div>
                    <div className="jwt-part payload">
                      <span className="part-label">Payload</span>
                      <code>{"{ sub: '1234567890', name: 'John Doe' }"}</code>
                    </div>
                    <div className="jwt-part signature">
                      <span className="part-label">Signature</span>
                      <code>HMACSHA256(...)</code>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeDemo === 'websocket' && (
            <div className="demo-panel">
              <h3>Real-Time WebSocket</h3>
              <p>Instant bidirectional communication</p>
              <div className="chat-box">
                <div className="messages-container">
                  {wsMessages.length === 0 ? (
                    <div className="empty-messages">
                      Send a message to get started...                    
                      </div>
                  ) : (
                    wsMessages.map(msg => (
                      <div 
                        key={msg.id} 
                        className={`message ${msg.sender === 'Tú' ? 'sent' : 'received'}`}
                      >
                        <div className="message-header">
                          <strong>{msg.sender}</strong>
                          <span className="message-time">{msg.timestamp}</span>
                        </div>
                        <div className="message-text">{msg.text}</div>
                      </div>
                    ))
                  )}
                </div>
                
                <div className="message-input">
                  <input
                    type="text"
                    placeholder="Type a message..."
                    value={wsMessage}
                    onChange={(e) => setWsMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendWebSocketMessage()}
                  />
                  <button 
                    className="btn-primary"
                    onClick={sendWebSocketMessage}
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeDemo === 'swagger' && (
            <div className="demo-panel">
              <h3>Swagger Documentation</h3>
              <p>Interactive API documentation using OpenAPI</p>  
              <div className="swagger-preview">
                <div className="swagger-header">
                  <h4>API Documentation</h4>
                  <span className="api-version">v1.0.0</span>
                </div>
                
                <div className="swagger-endpoints">
                  <div className="endpoint">
                    <span className="method get">GET</span>
                    <span className="path">/api/users</span>
                    <span className="description">Get User List</span>
                  </div>
                  <div className="endpoint">
                    <span className="method post">POST</span>
                    <span className="path">/api/users</span>
                    <span className="description">Create New User</span>
                  </div>
                  <div className="endpoint">
                    <span className="method put">PUT</span>
                    <span className="path">/api/users/:id</span>
                    <span className="description">Update User</span>
                  </div>
                  <div className="endpoint">
                    <span className="method delete">DELETE</span>
                    <span className="path">/api/users/:id</span>
                    <span className="description">Delete User</span>
                  </div>
                </div>

                {/* 
<div className="note">
  <strong>Note:</strong> Connect your backend to view the full documentation and test the endpoints live.
</div>
*/}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default Demos;
